import { useEffect, useState, FormEvent, ChangeEvent } from "react";
import {
  FiX,
  FiImage,
  FiVideo,
  FiEdit3,
  FiFileText,
  FiInfo,
  FiUpload,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import exercicies from "../../services/exercicies";
import { toast } from "react-toastify";
import Strings from "../../constants/Strings";
import utils from "../../helpers/utils";
import { Exercise, ExercicieModalProps } from "../../types";
import AuthenticatedImage from "../ui/AuthenticatedImage";

function ExercicieModal({
  isOpen,
  onBack,
  data,
  onSuccess = () => {},
}: ExercicieModalProps & { onSuccess?: () => void }) {
  const { getUser } = useAuth();
  const user = getUser();
  const professional_id = user?.id;

  const [form, setForm] = useState<Partial<Exercise>>({
    name: "",
    image: "",
    professional_id: professional_id ? Number(professional_id) : undefined,
    video: "",
    description: "",
    id: undefined,
  });

  const [imagePreview, setImagePreview] = useState<string>("");
  const [videoPreview, setVideoPreview] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [videoSectionExpanded, setVideoSectionExpanded] = useState(false);

  // Verifica se o exercício é público (all_view) e se o usuário não é admin
  const isReadOnly =
    form.id !== undefined &&
    form.id !== null &&
    (form.all_view === true || data?.all_view === true) &&
    !user?.is_admin;

  const submit = async (e: FormEvent) => {
    e.preventDefault();

    // Bloqueia o submit se o exercício for público e o usuário não for admin
    if (isReadOnly) {
      toast.error("Este exercício é público e não pode ser alterado.");
      return;
    }

    setLoading(true);

    if (form.id !== undefined && form.id !== null && form.id !== 0) {
      const result = await exercicies.update(String(form.id), form);
      setLoading(false);
      if (result.ok) {
        toast.success("Exercício atualizado com sucesso!");
        onSuccess();
        onBack();
      } else {
        toast.error(result.error ?? "Erro ao atualizar o exercício.");
      }
    } else {
      const body = {
        ...form,
        professional_id:
          form.professional_id ||
          (professional_id ? Number(professional_id) : 0),
        name: form.name || "",
      } as Exercise;

      const result = await exercicies.create(body);
      setLoading(false);
      if (result.ok) {
        toast.success("Exercício criado com sucesso!");
        onSuccess();
        onBack();
      } else {
        toast.error(result.error ?? "Erro ao criar o exercício.");
      }
    }
  };

  useEffect(() => {
    if (isOpen && data) {
      setForm((prevForm) => ({
        ...prevForm,
        id: undefined,
        professional_id: professional_id ? Number(professional_id) : undefined,
        ...data,
      }));
      setImagePreview(
        data.image_url ||
          utils.getUriImage(data.image ?? Strings.default_photo, "all_view"),
      );
      // Preview do vídeo: base64 (novo upload) ou URL do S3 (vídeo existente)
      if (data.video?.startsWith("data:")) {
        setVideoPreview(data.video);
      } else if (data.video) {
        setVideoPreview(
          utils.getUriImage(
            data.video,
            data.professional_id ? String(data.professional_id) : "all_view",
          ),
        );
      } else {
        setVideoPreview("");
      }
      // Se já tem vídeo, expande a seção para o usuário visualizar
      setVideoSectionExpanded(!!data.video);
    }
  }, [isOpen, data, professional_id]);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setForm({ ...form, image: base64String });
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Verifica o tamanho do arquivo (máximo 50MB)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        toast.error("O vídeo deve ter no máximo 50MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setForm({ ...form, video: base64String });
        setVideoPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[95vh] overflow-hidden border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark mt-auto sm:my-auto flex flex-col">
        {/* Header Premium com Gradiente */}
        <div className="relative border-b border-stroke bg-gradient-to-r from-purple-50 via-purple-50 to-purple-50 px-3 sm:px-6 py-3 sm:py-4 dark:border-strokedark dark:from-purple-950/30 dark:via-purple-950/30 dark:to-purple-950/30">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

          <button
            onClick={onBack}
            className="absolute right-2 top-2.5 sm:right-4 sm:top-4 z-10 rounded-xl p-1.5 sm:p-2 text-gray-500 outline-none transition-all hover:bg-white hover:text-gray-700 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:bg-boxdark dark:hover:text-gray-200 touch-manipulation"
            aria-label="Fechar modal"
          >
            <FiX size={18} className="sm:w-6 sm:h-6" />
          </button>

          <div className="relative flex items-center gap-2.5 sm:gap-3 pr-10 sm:pr-0">
            <div className="rounded-xl bg-gradient-to-br from-purple-600 to-purple-600 p-2 sm:p-2.5 text-white shadow-sm flex-shrink-0">
              {form.id ? (
                <FiEdit3 size={16} className="sm:w-6 sm:h-6" />
              ) : (
                <FiImage size={16} className="sm:w-6 sm:h-6" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white leading-tight">
                {form.id ? "Editar Exercício" : "Novo Exercício"}
              </h3>
              <p className="mt-0.5 text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-tight">
                {form.id
                  ? "Atualize as informações do exercício"
                  : "Preencha os dados para adicionar um novo exercício"}
              </p>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <form
          id="exercise-form"
          onSubmit={submit}
          className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-6"
        >
          <div className="space-y-3 sm:space-y-4">
            {/* Alerta de Acesso Restrito */}
            {isReadOnly && (
              <div className="rounded-xl sm:rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-amber-50 p-3 sm:p-4 dark:border-amber-700 dark:from-amber-900/30 dark:to-amber-900/30">
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <div className="rounded-lg sm:rounded-xl bg-amber-100 p-2 sm:p-2.5 dark:bg-amber-900/40 flex-shrink-0">
                    <FiInfo
                      className="text-amber-600 dark:text-amber-400"
                      size={18}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="mb-1 sm:mb-1 font-bold text-xs sm:text-sm text-amber-900 dark:text-amber-300">
                      Acesso Restrito
                    </h5>
                    <p className="text-xs sm:text-sm leading-relaxed text-amber-800 dark:text-amber-400">
                      Este exercício é público e não pode ser alterado.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Seção: Informações Básicas */}
            <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-3 sm:p-5 shadow-sm dark:border-strokedark dark:bg-boxdark">
              <div className="mb-3 sm:mb-4 flex items-center gap-2">
                <div className="h-0.5 sm:h-1 w-6 sm:w-8 rounded bg-purple-600"></div>
                <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  Informações Básicas
                </h4>
              </div>

              <div className="space-y-3 sm:space-y-4">
                {/* Nome do Exercício */}
                <div>
                  <label
                    htmlFor="name"
                    className="mb-1.5 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Nome do Exercício <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2.5 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                      <FiEdit3 size={14} className="sm:w-[18px] sm:h-[18px]" />
                    </span>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      disabled={isReadOnly}
                      value={form.name || ""}
                      onChange={({ target }: ChangeEvent<HTMLInputElement>) => {
                        setForm({ ...form, [target.name]: target.value });
                      }}
                      placeholder="Ex: Supino Reto, Agachamento..."
                      className="w-full max-w-full rounded-lg sm:rounded-xl border border-stroke bg-gray-50 py-2.5 sm:py-2.5 pl-9 sm:pl-11 pr-3 sm:pr-4 text-xs sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed box-border"
                    />
                  </div>
                </div>

                {/* Descrição */}
                <div>
                  <label
                    htmlFor="description"
                    className="mb-1.5 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Descrição (opcional)
                  </label>
                  <div className="relative">
                    <span className="absolute left-2.5 sm:left-4 top-3 sm:top-3.5 text-gray-400 pointer-events-none z-10">
                      <FiFileText
                        size={14}
                        className="sm:w-[18px] sm:h-[18px]"
                      />
                    </span>
                    <textarea
                      id="description"
                      name="description"
                      rows={4}
                      disabled={isReadOnly}
                      value={form.description || ""}
                      onChange={({
                        target,
                      }: ChangeEvent<HTMLTextAreaElement>) => {
                        setForm({ ...form, [target.name]: target.value });
                      }}
                      placeholder="Descreva o exercício, músculos trabalhados, dicas de execução..."
                      className="w-full max-w-full rounded-lg sm:rounded-xl border border-stroke bg-gray-50 py-2.5 sm:py-2.5 pl-9 sm:pl-11 pr-3 sm:pr-4 text-xs sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation resize-none disabled:opacity-60 disabled:cursor-not-allowed box-border"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Seção: Vídeo do Exercício (minimizada por default) */}
            <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark overflow-hidden">
              <button
                type="button"
                onClick={() => setVideoSectionExpanded((prev) => !prev)}
                className="w-full flex items-center justify-between gap-2 p-3 sm:p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="h-0.5 sm:h-1 w-6 sm:w-8 rounded bg-purple-600"></div>
                  <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    Vídeo do Exercício (opcional)
                  </h4>
                  {(videoPreview || form.video) && (
                    <span className="text-[10px] sm:text-xs font-medium text-purple-600 dark:text-purple-400">
                      • Vídeo adicionado
                    </span>
                  )}
                </div>
                {videoSectionExpanded ? (
                  <FiChevronUp size={18} className="text-gray-500 shrink-0" />
                ) : (
                  <FiChevronDown size={18} className="text-gray-500 shrink-0" />
                )}
              </button>

              {videoSectionExpanded && (
                <div className="px-3 sm:px-5 pb-3 sm:pb-5 pt-0">
                  {/* Preview do Vídeo */}
                  <div className="mb-3 sm:mb-4 relative flex h-48 sm:h-64 w-full items-center justify-center overflow-hidden rounded-lg sm:rounded-xl border-2 border-dashed border-stroke bg-gradient-to-br from-gray-50 to-gray-100 dark:border-strokedark dark:from-gray-800 dark:to-gray-900">
                    {videoPreview ? (
                      <>
                        <video
                          src={videoPreview}
                          controls
                          className="h-full w-full object-contain"
                        >
                          <track kind="captions" />
                        </video>
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => {
                              setForm({ ...form, video: "" });
                              setVideoPreview("");
                            }}
                            className="absolute top-2 right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-red-600 transition-colors z-10"
                            aria-label="Remover vídeo"
                          >
                            <FiX size={16} />
                          </button>
                        )}
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          if (isReadOnly) {
                            e.preventDefault();
                            return;
                          }
                          const input = document.getElementById(
                            "exercise-video-input",
                          ) as HTMLInputElement;
                          if (input) {
                            input.click();
                          }
                        }}
                        disabled={isReadOnly}
                        className="flex flex-col items-center gap-2 sm:gap-3 p-4 w-full h-full justify-center group focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        aria-label="Adicionar vídeo do exercício"
                      >
                        <div className="rounded-xl bg-gray-200 p-3 sm:p-4 dark:bg-gray-700 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/40 transition-colors">
                          <FiVideo
                            size={32}
                            className="text-gray-400 sm:w-12 sm:h-12 group-hover:text-purple-500 transition-colors"
                          />
                        </div>
                        <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 group-hover:text-purple-500 transition-colors">
                          Nenhum vídeo selecionado
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-400 text-center group-hover:text-purple-400 transition-colors">
                          Clique para adicionar um vídeo demonstrativo
                        </p>
                      </button>
                    )}
                  </div>

                  {/* Botão de Upload / Substituir Vídeo */}
                  <label
                    className={`group relative flex w-full items-center justify-center gap-2 sm:gap-2.5 rounded-lg sm:rounded-xl border-2 border-dashed border-stroke bg-gray-50 px-4 py-3 sm:py-3.5 text-xs sm:text-base font-semibold text-gray-700 transition-all hover:border-purple-500 hover:bg-purple-50 hover:text-purple-600 dark:border-strokedark dark:bg-boxdark/50 dark:text-gray-300 dark:hover:border-purple-500 dark:hover:bg-purple-950/20 dark:hover:text-purple-400 touch-manipulation ${
                      isReadOnly
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer"
                    }`}
                  >
                    <FiUpload
                      size={16}
                      className="sm:w-5 sm:h-5 transition-transform group-hover:scale-110"
                    />
                    {videoPreview || form.video
                      ? "Substituir Vídeo"
                      : "Selecionar Vídeo"}
                    <input
                      id="exercise-video-input"
                      type="file"
                      accept="video/*"
                      onChange={handleVideoChange}
                      disabled={isReadOnly}
                      className="hidden"
                    />
                  </label>

                  <p className="mt-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 text-center">
                    Formatos aceitos: MP4, MOV, WebM (máx. 50MB)
                  </p>
                </div>
              )}
            </div>

            {/* Seção: Imagem do Exercício */}
            <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-3 sm:p-5 shadow-sm dark:border-strokedark dark:bg-boxdark">
              <div className="mb-3 sm:mb-4 flex items-center gap-2">
                <div className="h-0.5 sm:h-1 w-6 sm:w-8 rounded bg-purple-600"></div>
                <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  Imagem do Exercício
                </h4>
              </div>

              {/* Preview da Imagem */}
              <div className="mb-3 sm:mb-4 relative flex h-48 sm:h-64 w-full items-center justify-center overflow-hidden rounded-lg sm:rounded-xl border-2 border-dashed border-stroke bg-gradient-to-br from-gray-50 to-gray-100 dark:border-strokedark dark:from-gray-800 dark:to-gray-900">
                {form.image ? (
                  <>
                    <AuthenticatedImage
                      fileName={imagePreview}
                      alt="Preview do exercício"
                      className="h-full w-full object-contain"
                    />
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => {
                          setForm({ ...form, image: "" });
                          setImagePreview("");
                        }}
                        className="absolute top-2 right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-red-600 transition-colors z-10"
                        aria-label="Remover imagem"
                      >
                        <FiX size={16} />
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      if (isReadOnly) {
                        e.preventDefault();
                        return;
                      }
                      const input = document.getElementById(
                        "exercise-image-input",
                      ) as HTMLInputElement;
                      if (input) {
                        input.click();
                      }
                    }}
                    disabled={isReadOnly}
                    className="flex flex-col items-center gap-2 sm:gap-3 p-4 w-full h-full justify-center group focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    aria-label="Adicionar imagem do exercício"
                  >
                    <div className="rounded-xl bg-gray-200 p-3 sm:p-4 dark:bg-gray-700 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/40 transition-colors">
                      <FiImage
                        size={32}
                        className="text-gray-400 sm:w-12 sm:h-12 group-hover:text-purple-500 transition-colors"
                      />
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 group-hover:text-purple-500 transition-colors">
                      Nenhuma imagem selecionada
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-400 text-center group-hover:text-purple-400 transition-colors">
                      Clique para adicionar uma imagem demonstrativa
                    </p>
                  </button>
                )}
              </div>

              {/* Botão de Upload */}
              <label
                className={`group relative flex w-full items-center justify-center gap-2 sm:gap-2.5 rounded-lg sm:rounded-xl border-2 border-dashed border-stroke bg-gray-50 px-4 py-3 sm:py-3.5 text-xs sm:text-base font-semibold text-gray-700 transition-all hover:border-purple-500 hover:bg-purple-50 hover:text-purple-600 dark:border-strokedark dark:bg-boxdark/50 dark:text-gray-300 dark:hover:border-purple-500 dark:hover:bg-purple-950/20 dark:hover:text-purple-400 touch-manipulation ${
                  isReadOnly
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer"
                }`}
              >
                <FiUpload
                  size={16}
                  className="sm:w-5 sm:h-5 transition-transform group-hover:scale-110"
                />
                Selecionar Imagem
                <input
                  id="exercise-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={isReadOnly}
                  className="hidden"
                />
              </label>

              <p className="mt-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 text-center">
                Formatos aceitos: JPG, PNG, GIF (máx. 5MB)
              </p>
            </div>

            {/* Info Box Premium */}
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-50 p-3 sm:p-4 dark:border-purple-800 dark:from-purple-900/20 dark:to-purple-900/20">
              <div className="absolute right-0 top-0 h-16 sm:h-24 w-16 sm:w-24 bg-gradient-to-br from-purple-500/10 to-purple-500/10 blur-sm"></div>

              <div className="relative flex items-start gap-2.5 sm:gap-3">
                <div className="rounded-lg sm:rounded-xl bg-purple-100 p-2 sm:p-2.5 dark:bg-purple-900/40 flex-shrink-0">
                  <FiInfo
                    className="text-purple-600 dark:text-purple-400"
                    size={18}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="mb-1 sm:mb-1 font-bold text-xs sm:text-sm text-purple-900 dark:text-purple-300">
                    Dicas para um bom cadastro
                  </h5>
                  <p className="text-xs sm:text-sm leading-relaxed text-purple-800 dark:text-purple-400">
                    Use imagens claras que demonstrem a execução correta do
                    exercício. Adicione uma descrição detalhada com dicas de
                    forma e músculos trabalhados para auxiliar os alunos.
                  </p>
                  <div className="mt-2 sm:mt-2.5 flex flex-wrap gap-1.5 sm:gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      ✓ Imagem demonstrativa
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      ✓ Vídeo tutorial
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      ✓ Descrição completa
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer Sticky com Botões */}
        <div className="sticky bottom-0 border-t border-stroke bg-white px-3 sm:px-6 py-3 sm:py-2 dark:border-strokedark dark:bg-boxdark shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] sm:shadow-none">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <p className="hidden sm:block text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Campos marcados com <span className="text-red-500">*</span> são
              obrigatórios
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2.5 w-full sm:w-auto">
              <button
                type="button"
                onClick={onBack}
                disabled={loading}
                className="w-full sm:w-auto order-2 sm:order-1 rounded-lg sm:rounded-xl border-2 border-stroke px-4 sm:px-5 py-2.5 sm:py-2.5 text-sm sm:text-base font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-50 dark:border-strokedark dark:text-white dark:hover:bg-meta-4 touch-manipulation active:scale-95 sm:active:scale-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="exercise-form"
                disabled={loading || isReadOnly}
                className="w-full sm:w-auto order-1 sm:order-2 inline-flex items-center justify-center gap-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-purple-600 to-purple-600 px-5 sm:px-6 py-2.5 sm:py-2.5 text-sm sm:text-base font-semibold text-white shadow-sm transition-all hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:hover:scale-100 touch-manipulation active:scale-95 sm:active:scale-105"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 sm:h-5 sm:w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    {form.id ? "Atualizando..." : "Cadastrando..."}
                  </>
                ) : (
                  <>
                    {form.id ? (
                      <FiEdit3 size={18} className="sm:w-5 sm:h-5" />
                    ) : (
                      <FiImage size={18} className="sm:w-5 sm:h-5" />
                    )}
                    {form.id ? "Atualizar" : "Criar"} Exercício
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExercicieModal;
